import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Select
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { log } from '../../utils/logger'
import { createDashboardRolesApi } from './../../utils/api-interface'
import { UpdateDashboardProfileRequest, DashboardProfile, DashboardRolesList, DashboardProfilesList } from '../../services/network'
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog/DeleteConfirmationDialog'
import { getErrorMessage } from '../../utils/utils'

interface IModifyDashboardProfileRoleModalProps {
  isOpen: boolean
  onClose: () => void
  dashboardProfileLoaded: DashboardProfile
  setIsModifyProfileModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  roles: DashboardRolesList
  setProfiles: React.Dispatch<React.SetStateAction<DashboardProfilesList | undefined>>
}

function ModifyDashboardProfileRoleModal({
  isOpen,
  onClose,
  dashboardProfileLoaded,
  setIsModifyProfileModalOpen,
  roles,
  setProfiles
}: IModifyDashboardProfileRoleModalProps): JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(isOpen)
  const toast = useToast()
  const [firstName, setFirstName] = useState('')
  const [loading, isLoading] = useState(false)

  const handleFirstName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFirstName(e.target.value)
  }
  const [lastName, setLastName] = useState('')
  const handleLastName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setLastName(e.target.value)
  }
  const [email, setEmail] = useState('')
  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value)
  }
  const [roleSelected, setRoleSelected] = useState('')

  const [updateDashboardProfileRequest, setUpdateDashboardProfileRequest] = useState<UpdateDashboardProfileRequest>()
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(true)

  const handleRoleChange = (e: { target: { value: React.SetStateAction<string> } }): void => {
    setRoleSelected(e.target.value)
  }

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const handleCloseDialog = (): void => {
    setIsDeleteDialogOpen(false)
  }
  const handleDeleteButton = (): void => {
    setIsDeleteDialogOpen(true)
  }

  const deleteProfile = async (): Promise<void> => {
    const dashboardRolesApi = createDashboardRolesApi()
    dashboardRolesApi
      .deleteDashboardProfile(dashboardProfileLoaded.id)
      .then((response) => {
        log.debug(response.data)
        setProfiles((prevState) => {
          if (!prevState) return prevState
          const updatedProfiles = prevState.dashboardProfiles.filter((profile) => profile.id !== dashboardProfileLoaded.id)
          return { ...prevState, dashboardProfiles: updatedProfiles }
        })
        setIsModifyProfileModalOpen(false)
      })
      .catch((error) => {
        log.error('Error deleting activity', error)
      })

    setIsDeleteDialogOpen(false)
  }

  const handleClose = useCallback((): void => {
    if (loading) {
      return
    }
    onClose()
    setFirstName('')
    setLastName('')
    setEmail('')
    setRoleSelected('')
    setUpdateDashboardProfileRequest(undefined)
  }, [loading, onClose])

  const modifyNewDashboardProfile = useCallback(async (): Promise<void> => {
    if (loading) {
      return
    }
    if (updateDashboardProfileRequest) {
      try {
        isLoading(true)
        const dashboardRolesApi = createDashboardRolesApi()
        const response = await dashboardRolesApi.updateDashboardProfile(dashboardProfileLoaded.id, updateDashboardProfileRequest)
        log.debug(response.data)
        const updatedProfile = response.data

        // Update the profiles in the state
        setProfiles((prevState) => {
          if (!prevState) return prevState

          const { dashboardProfiles } = prevState

          const updatedProfiles = dashboardProfiles.map(
            (profile) =>
              profile.id === updatedProfile.id
                ? updatedProfile // Replace the old profile with the updated one
                : profile // Keep the other profiles unchanged
          )

          return {
            ...prevState,
            dashboardProfiles: updatedProfiles
          }
        })
        toast({
          title: t('dashboard_profile_updated_toast'),
          status: 'success',
          duration: 4000,
          isClosable: true
        })
        handleClose()
      } catch (error) {
        toast({
          title: t('dashboard_profile_updated_error_toast'),
          description: `${getErrorMessage(error)}`,
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      }
      setConfirmButtonDisabled(false)
    }
    isLoading(true)
  }, [dashboardProfileLoaded.id, handleClose, loading, setProfiles, t, toast, updateDashboardProfileRequest])

  useEffect(() => {
    if (firstName && lastName && email && roleSelected) {
      setConfirmButtonDisabled(false)
      setUpdateDashboardProfileRequest({
        firstName: firstName,
        lastName: lastName,
        email: email,
        roleId: roleSelected
      })
    } else {
      setConfirmButtonDisabled(true)
    }
  }, [firstName, lastName, email, roleSelected])

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    setFirstName(dashboardProfileLoaded.firstName)
    setLastName(dashboardProfileLoaded.lastName)
    setEmail(dashboardProfileLoaded.email)
    setRoleSelected(dashboardProfileLoaded.role.id)
  }, [dashboardProfileLoaded.email, dashboardProfileLoaded.firstName, dashboardProfileLoaded.lastName, dashboardProfileLoaded.role.id, open])

  useEffect(() => {
    eva.replace()
  })

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered={true}>
        <ModalOverlay />
        <ModalContent marginBottom={'0px'} borderRadius={'26px 26px 0px 0px'}>
          <ModalHeader fontSize='24px' fontWeight='700'>
            {t('modify_profile')}
          </ModalHeader>

          <ModalBody>
            <FormControl id='firstName'>
              <FormLabel variant={'primary'}>{t('first_name')}</FormLabel>
              <Input bg='white' value={firstName} onChange={handleFirstName} />
            </FormControl>

            <FormControl id='lastName' mt={'10px'}>
              <FormLabel variant={'primary'}>{t('last_name')}</FormLabel>
              <Input bg='white' value={lastName} onChange={handleLastName} />
            </FormControl>

            <FormControl id='email' mt={'10px'}>
              <FormLabel variant={'primary'}>{t('email')}</FormLabel>
              <Input bg='white' value={email} isDisabled={true} onChange={handleEmail} />
            </FormControl>

            <FormControl id='role'>
              <FormLabel variant={'primary'} marginTop={'10px'}>
                {t('role')}
              </FormLabel>
              <Select variant={'primaryBlack'} placeholder={t('placeholder_role_select')} onChange={handleRoleChange} value={roleSelected}>
                {roles &&
                  roles.dashboardRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {t(`sidebar_roles.${role.title}`)}
                    </option>
                  ))}
              </Select>
            </FormControl>
            <Button
              marginTop={'20px'}
              padding={'0px'}
              key='deleteaccount'
              onClick={(): void => {
                handleDeleteButton()
              }}
              gap={'10px'}>
              <i data-eva={'trash-2'} data-eva-fill={'#353F48'}></i>
              <Text
                marginLeft={'5px'}
                fontWeight='600'
                fontSize='16px'
                lineHeight='22.4px'
                color={'greyCold200'}
                _hover={{
                  color: 'black'
                }}>
                {t('delete_permanently')}
              </Text>
            </Button>
          </ModalBody>

          <ModalFooter justifyContent='center'>
            <Button
              variant='outline'
              width={'50%'}
              height='50px'
              color={'primary.400'}
              borderColor={'primary.400'}
              onClick={handleClose}
              margin={'5px'}
              isDisabled={loading}>
              {t('cancel')}
            </Button>
            <Button
              variant={'primary'}
              width={'50%'}
              height='50px'
              isDisabled={confirmButtonDisabled}
              onClick={modifyNewDashboardProfile}
              margin={'5px'}
              isLoading={loading}>
              {t('save')}
            </Button>
          </ModalFooter>
        </ModalContent>
        <DeleteConfirmationDialog isOpen={isDeleteDialogOpen} onClose={handleCloseDialog} onConfirm={deleteProfile} type='profile' />
      </Modal>
    </>
  )
}

export { ModifyDashboardProfileRoleModal }
